import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { inject, observer } from 'mobx-react'
import gsap from 'gsap'
import { drawCircle } from '../lib/draw-canvas'
import { scaleByDevicePixelRatio } from '../../../lib/canvas-utils'
import { StateType } from '../state'
import { useAnimationLoop } from '../../../lib/hooks/useAnimationLoop'
import { useParentSize } from '../../../lib/hooks/useParentSize'
import { useCircleChallengesDrawInfo } from '../useCircleChallengesDrawInfo'
import { clamp, zipObject } from 'lodash-es'
import { drawChallengeLabel } from '../../../lib/ChallengeLabel'
import { Challenge, CHALLENGES } from '../../../lib/constants'
import { getMousePosition } from '../../../lib/event-utils'
import { SliderStep } from '../../../components/ui/SliderStep'
import { Container } from './Container'
import { LulucfCaption } from '../../../components/LulucfCaption'
import { lulucfEmPerc } from '../data'

const ANIMATION_DURATION = 0.6
const DIMENSIONS = { w: { min: 250, max: 810 }, h: { min: 375, max: 420 } }
const IMAGE_PADDING = 12
const HOVER_FILL_OPACITY = 0.2

interface Props {
  state?: StateType
  isChallengeSwitchable: boolean
}

export const CirclesChallenges: React.FC<Props> = inject('state')(
  observer(({ state, isChallengeSwitchable }) => {
    const { ChallengesState, Circles } = state
    const { setChallenge: setChallengeForEveryone, challenge, debug } = Circles
    const {
      selectedChallege,
      setSelectedChallenge,
      setHoveredChallenge,
      hoveredChallenge,
    } = ChallengesState

    const canvasRef = useRef<HTMLCanvasElement>()

    const [{ width: parentWidth, height: parentHeight }] = useParentSize(canvasRef)
    const canvasWidth = parentWidth
    const canvasHeight = clamp(canvasWidth, DIMENSIONS.h.min, DIMENSIONS.h.max)
    const imageWidth = clamp(canvasWidth - 2 * IMAGE_PADDING, DIMENSIONS.w.min, DIMENSIONS.w.max)
    const imageHeight = clamp(canvasWidth, DIMENSIONS.h.min, DIMENSIONS.h.max)

    const circlesDrawInfo = useCircleChallengesDrawInfo(
      canvasWidth,
      canvasHeight,
      imageWidth,
      imageHeight,
      selectedChallege
    )

    function setup() {
      const ctx = canvasRef.current?.getContext('2d')
      scaleByDevicePixelRatio(canvasRef.current, ctx, canvasWidth, canvasHeight)
      onMountAnimations()
    }

    function onMountAnimations() {
      gsap
        .timeline({
          onStart: () => startLoop(),
          onComplete: () => stopLoop(),
        })
        .to(circlesDrawInfo, {
          animationRadius: 1,
          duration: ANIMATION_DURATION,
        })
        .to(circlesDrawInfo, {
          animationPosition: 1,
          duration: ANIMATION_DURATION,
          delay: -ANIMATION_DURATION,
        })
    }

    function highlightCircle(
      event: React.MouseEvent<HTMLCanvasElement, MouseEvent>,
      setChallenge: (c: Challenge) => void
    ) {
      const { left, top } = canvasRef.current.getBoundingClientRect()
      const [xPos, yPos] = getMousePosition(event, [left, top])
      const hoveredChallenge = circlesDrawInfo.getHoveredCircleChallenge(xPos, yPos)

      if (!hoveredChallenge) return

      setChallenge(hoveredChallenge)
    }

    const [draw, startLoop, stopLoop] = useAnimationLoop(() => {
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return
      ctx.clearRect(0, 0, circlesDrawInfo.canvasWidth, circlesDrawInfo.canvasHeight)

      Object.entries(circlesDrawInfo.datasetWithDrawInfo).forEach(([label, circle]) => {
        const isSelected = label === ChallengesState.selectedChallege
        const isHovered = label === ChallengesState.hoveredChallenge

        const fillOpacity =
          isHovered && !isSelected
            ? HOVER_FILL_OPACITY
            : circlesDrawInfo.animationFillOpacity[label]
        const strokeOpacity = isSelected ? 1 : isHovered ? 0 : 1

        drawCircle(ctx, circle, { fillOpacity, strokeOpacity })
      })

      const highlightedCircle =
        circlesDrawInfo.datasetWithDrawInfo[ChallengesState.selectedChallege]

      drawChallengeLabel(ctx, {
        x: ctx.canvas.clientWidth / 2,
        y: 25,
        challenge: highlightedCircle.label as Challenge,
        percentage: highlightedCircle.percentage,
        align: 'center center',
        fontSize: 16,
        showShadow: false,
      })
    }, [])

    // First render: reset challenge to the one of this instance
    useLayoutEffect(() => {
      if (!isChallengeSwitchable) setSelectedChallenge(challenge as Challenge)
    }, [])

    useEffect(() => {
      if (!parentWidth || !parentHeight) return
      setup()
      draw()
    }, [parentWidth, parentHeight])

    useEffect(() => {
      if (!parentWidth || !parentHeight) return
      draw()
    }, [hoveredChallenge])

    useEffect(() => {
      const newOpacities = zipObject(
        CHALLENGES,
        CHALLENGES.map((c) =>
          c === selectedChallege ? 1 : c === hoveredChallenge ? HOVER_FILL_OPACITY : 0
        )
      )

      gsap.to(circlesDrawInfo.animationFillOpacity, {
        ...newOpacities,
        duration: ANIMATION_DURATION,
        onStart: () => startLoop(),
        onComplete: () => stopLoop(),
      })
    }, [selectedChallege, hoveredChallenge])

    return (
      <Container>
        <canvas
          className={`${debug ? 'ba b--red' : ''}`}
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onClick={(event) => {
            highlightCircle(event, (c) => {
              setSelectedChallenge(c)
              if (isChallengeSwitchable) setChallengeForEveryone(c)
            })
          }}
          onMouseMove={(event) => {
            highlightCircle(event, setHoveredChallenge)
          }}
          onMouseLeave={() => setHoveredChallenge(null)}
        />
        {canvasWidth !== 0 && (
          <>
            <SliderStep
              className="mt3"
              stepsCounter={Object.keys(circlesDrawInfo.datasetWithDrawInfo).length}
              selectedStep={circlesDrawInfo.datasetWithDrawInfo[selectedChallege].index}
              onChange={(value) => {
                const drawInfo = Object.values(circlesDrawInfo.datasetWithDrawInfo)
                const challenge = drawInfo.find((info) => info.index === value)

                setSelectedChallenge(challenge.label)
                if (isChallengeSwitchable) setChallengeForEveryone(challenge.label)
              }}
            />
            <div className="-lulucf-caption" style={{ height: 48 }}>
              {selectedChallege === 'agriculture' && (
                <LulucfCaption
                  classNames="lh-copy justify-center"
                  emissions={lulucfEmPerc.emission_mmt_co2}
                />
              )}
            </div>
          </>
        )}
      </Container>
    )
  })
)
